import { useState } from "react";
import { Button, cn } from "@sfc/ui";
import { DownloadIcon, PaperclipIcon } from "lucide-react";
import type { AnswerValue, FileRef, Question } from "../types";
import { answerableQuestions, answerToText } from "../registry";
import { isEmptyValue } from "../logic";

/**
 * An answer as something to read rather than edit.
 *
 * Two surfaces show a response without offering to change it — the recap an
 * applicant sees after submitting, and the admin's view of a response that
 * came in — and they must agree about what an answer says. So the rendering
 * lives here once, next to `answerToText`, rather than being written twice
 * and drifting the first time a question type gains a shape.
 *
 * Text is `answerToText`, the same definition everything downstream of a form
 * reads answers through. Files are the exception: their text is a list of
 * filenames, which is the right *words* but loses the only thing a reader
 * wants from an attachment, so they render as rows that fetch a download.
 */

/**
 * Mints a short-lived download URL for one attached file.
 *
 * The same port the upload control uses (`FileUploadService.downloadUrl`),
 * for the same reason: this package knows nothing about tRPC, response ids or
 * who is allowed to read the bytes. Omitted where nothing can grant one — a
 * preview has no stored files — and then filenames render as plain text.
 */
export type DownloadUrlFor = (file: FileRef) => Promise<string>;

function FileRow({
  file,
  downloadUrl,
}: {
  file: FileRef;
  downloadUrl?: DownloadUrlFor;
}) {
  const [fetching, setFetching] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function download() {
    if (!downloadUrl) return;
    setFetching(true);
    setError(null);
    try {
      // An attachment URL downloads in place; the page stays. Same move the
      // upload control makes.
      window.location.assign(await downloadUrl(file));
    } catch {
      setError("The download link couldn't be fetched. Try again.");
    } finally {
      setFetching(false);
    }
  }

  return (
    <div className="flex flex-col gap-1">
      <div className="flex max-w-xl items-center gap-2 rounded-md border border-border px-3 py-2 text-sm">
        <PaperclipIcon
          aria-hidden
          className="size-4 shrink-0 text-muted-foreground"
        />
        <span className="min-w-0 flex-1 truncate">{file.filename}</span>
        {downloadUrl ? (
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            // 44px on touch (DESIGN.md), as a real box rather than a grown
            // ::after — see the note on the uploader's matching control.
            className="pointer-coarse:size-11"
            aria-label={`Download ${file.filename}`}
            isLoading={fetching}
            disabled={fetching}
            onClick={() => void download()}
          >
            <DownloadIcon />
          </Button>
        ) : null}
      </div>
      {error ? <p className="label-sm text-destructive">{error}</p> : null}
    </div>
  );
}

/** One answer's value, with no label — the label is the caller's to place. */
export function AnswerReadout({
  question,
  value,
  downloadUrl,
  unansweredLabel = "Not answered",
}: {
  question: Question;
  value: AnswerValue | undefined;
  downloadUrl?: DownloadUrlFor;
  /** What a question that was asked and left blank reads as. */
  unansweredLabel?: string;
}) {
  // The shared blankness predicate, not a text comparison: a whitespace-only
  // optional answer submits as "not answered", and a reader must be told the
  // same thing validation decided.
  if (value === undefined || isEmptyValue(value)) {
    return (
      <p className="body-md text-muted-foreground italic">{unansweredLabel}</p>
    );
  }

  if (value.kind === "files") {
    return (
      <div className="flex flex-col gap-1.5">
        {value.files.map((file) => (
          <FileRow key={file.fileId} file={file} downloadUrl={downloadUrl} />
        ))}
      </div>
    );
  }

  return (
    <p className="body-md whitespace-pre-line">
      {answerToText(question, value)}
    </p>
  );
}

/**
 * A run of question-and-answer pairs.
 *
 * `questions` is whatever the caller decided should be listed — the two
 * callers want different sets, and neither is derivable from the other here:
 * the applicant's recap lists what they answered, while an admin reading an
 * application wants every question that was *asked*, blanks included, because
 * an omitted question reads as one that was never on the form.
 */
export function AnswerReadoutList({
  questions,
  answers,
  downloadUrl,
  className,
}: {
  questions: readonly Question[];
  answers: Readonly<Record<string, AnswerValue | undefined>>;
  downloadUrl?: DownloadUrlFor;
  className?: string;
}) {
  // Narrowed here as well as by the callers, because this is the component
  // that would draw the mistake: a text block has no answer, so handing one
  // to `AnswerReadout` renders the form's own instructions as a question the
  // applicant left blank. Filtering at the point of render is what makes that
  // impossible for the next caller rather than merely fixed for this one.
  const readable = answerableQuestions(questions);
  return (
    <div className={cn("flex flex-col gap-4", className)}>
      {readable.map((question) => (
        <div key={question.id} className="flex flex-col gap-0.5">
          <p className="label-sm text-muted-foreground">
            {question.label || "Untitled question"}
          </p>
          <AnswerReadout
            question={question}
            value={answers[question.id]}
            downloadUrl={downloadUrl}
          />
        </div>
      ))}
    </div>
  );
}

/**
 * The questions from a walk that carry an answer — the applicant's recap,
 * which lists what was recorded rather than what was left blank.
 *
 * A narrowing of `answerableQuestions`, deliberately expressed as one: the
 * recap and the reviewer's full read differ only in whether blanks are kept,
 * and writing that as two independent filters is how they drift.
 */
export function answeredQuestions(
  asked: readonly Question[],
  answers: Readonly<Record<string, AnswerValue | undefined>>,
): Question[] {
  return answerableQuestions(asked).filter(
    (question) => !isEmptyValue(answers[question.id]),
  );
}
